/**
 * Authenticated fetch wrapper.
 *
 * Automatically attaches a Bearer token from MSAL to every outgoing
 * request so the backend can validate the user's identity.
 * Also attaches the X-Session-Token header for single-session enforcement.
 *
 * Session enforcement is gated behind a "ready" flag so that API calls
 * made before /api/auth/me returns (e.g. dashboard stats) don't falsely
 * trigger the concurrent-session dialog with a stale or cross-user token.
 */
import { msalInstance } from '../contexts/AuthContext';
import { loginRequest, msalConfig } from '../authConfig';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/** sessionStorage key used to show a one-time notice on the Login page. */
export const AUTH_NOTICE_KEY = 'auth_notice';

/** Fallback text if the backend response omits a message. */
export const ACCOUNT_DEACTIVATED_MESSAGE =
  'Your account is deactivated, please contact the administrator to reactivate your account';
let _deactivationHandled = false;

// In-memory token cache to avoid repeated acquireTokenSilent calls
let cachedToken: string | null = null;
let tokenExpiry = 0;

// ── Single-session enforcement state ───────────────────────────
// _sessionReady is set to true AFTER /api/auth/me (or dev-login) has
// returned a valid session token.  Until then, X-Session-Token is not
// sent and 409 responses are not treated as concurrent-session events.
let _sessionReady = false;

// Callback invoked when the backend rejects the request due to a
// concurrent session (HTTP 409 with error=concurrent_session).
let _onConcurrentSession: (() => void) | null = null;

/**
 * Register a callback that fires when the backend detects a concurrent
 * session.  The AuthContext sets this once on mount so the user can be
 * shown a dialog and redirected to the login page.
 */
export function onConcurrentSession(cb: () => void): void {
  _onConcurrentSession = cb;
}

/**
 * Mark the session as initialised — called after /api/auth/me or
 * /api/auth/dev-login returns a session token.  Until this is called,
 * authFetch will NOT send X-Session-Token and will NOT trigger the
 * concurrent-session dialog.
 */
export function markSessionReady(): void {
  _sessionReady = true;
}

/**
 * Reset session state on logout so the next login starts clean.
 */
export function resetSessionState(): void {
  _sessionReady = false;
}

/**
 * Acquire an access token silently with in-memory caching.
 * Returns a cached token if it has at least 60 seconds before expiry.
 * In dev-login mode, returns the stored dev token instead of MSAL.
 */
async function getToken(): Promise<string | null> {
  // Dev-token takes precedence over MSAL
  const devToken = sessionStorage.getItem('dev_token');
  if (devToken) return devToken;

  // Return cached MSAL token if still valid (60s buffer)
  if (cachedToken && Date.now() < tokenExpiry - 60_000) {
    return cachedToken;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account: accounts[0],
    });
    cachedToken = response.accessToken;
    tokenExpiry = response.expiresOn?.getTime() ?? (Date.now() + 3600_000);
    return cachedToken;
  } catch {
    try {
      await msalInstance.acquireTokenRedirect(loginRequest);
      // Page redirects, this line won't execute
      return null;
    } catch {
      return null;
    }
  }
}

/**
 * Drop-in replacement for `fetch()` that injects the Authorization header
 * and the X-Session-Token header for single-session enforcement.
 *
 * Usage:
 *   import { authFetch } from '../api/authFetch';
 *   const res = await authFetch('/api/assessments/questions?domain=Azure');
 *
 * Relative paths are resolved against the API base URL.
 * Absolute URLs (http://...) are used as-is.
 */
export async function authFetch(
  input: string,
  init?: (RequestInit & { timeoutMs?: number }),
): Promise<Response> {
  const token = await getToken();

  // Resolve relative paths against API_BASE
  const url = input.startsWith('http') ? input : `${API_BASE}${input}`;

  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // Attach session token for single-session enforcement.
  // Only sent AFTER /me has initialised the session to avoid sending a
  // stale token from a different user or previous login.
  if (_sessionReady) {
    const sessionToken = localStorage.getItem('session_token');
    if (sessionToken) {
      headers.set('X-Session-Token', sessionToken);
    }
  }
  if (!headers.has('Content-Type') && init?.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  // Add a timeout to prevent indefinite hanging on slow/unreachable backend.
  // If the caller already provides their own signal (e.g. for cancellation),
  // respect it and don't add our own timeout.
  // Use 3 minutes for POST requests (AI generation can be slow) and 30s for GETs.
  const callerSignal = init?.signal;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let fetchSignal: AbortSignal | undefined = callerSignal || undefined;
  let timedOut = false;

  if (!callerSignal) {
    const timeoutMs = init?.timeoutMs ?? (init?.method === 'POST' ? 300_000 : 60_000);
    const controller = new AbortController();
    timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort('Request timeout');
    }, timeoutMs);
    fetchSignal = controller.signal;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers,
      signal: fetchSignal,
    });
  } catch (error: any) {
    if (error?.name === 'AbortError' || fetchSignal?.aborted) {
      if (timedOut) {
        throw new Error('Request timed out. ERA generation may still be processing; try again with fewer files or retry shortly.');
      }
      throw new Error('Request was cancelled. Please retry.');
    }
    throw new Error(`Failed to reach backend API at ${url}. Check backend server and CORS/API URL configuration.`);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  // Handle concurrent session rejection (409) — only after session is ready
  if (response.status === 409 && _sessionReady) {
    try {
      const body = await response.clone().json();
      if (body.error === 'concurrent_session' && _onConcurrentSession) {
        _onConcurrentSession();
      }
    } catch { /* ignore parse errors */ }
  }

  // Account deactivated by an admin — force sign-out and show a notice.
  if (response.status === 403 && !_deactivationHandled) {
    try {
      const body = await response.clone().json();
      if (body.account_status === 'deactivated') {
        _deactivationHandled = true;
        const isDevMode = sessionStorage.getItem('dev_mode') === 'true';
        sessionStorage.removeItem('dev_mode');
        sessionStorage.removeItem('dev_token');
        sessionStorage.removeItem('portal_redirected');
        localStorage.removeItem('session_token');
        sessionStorage.setItem(AUTH_NOTICE_KEY, body.message || ACCOUNT_DEACTIVATED_MESSAGE);
        cachedToken = null;
        tokenExpiry = 0;
        _sessionReady = false;
        if (isDevMode) {
          window.location.reload();
        } else {
          msalInstance
            .logoutRedirect({ postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri })
            .catch(() => window.location.reload());
        }
      }
    } catch { /* ignore parse errors */ }
  }

  // On 401, clear stale auth state so the user is redirected to login
  if (response.status === 401) {
    const isDevMode = sessionStorage.getItem('dev_mode') === 'true';
    if (isDevMode) {
      sessionStorage.removeItem('dev_mode');
      sessionStorage.removeItem('dev_token');
      localStorage.removeItem('session_token');
      window.location.reload();
    } else {
      // Clear any stale MSAL token cache
      cachedToken = null;
      tokenExpiry = 0;
    }
  }

  return response;
}

export { API_BASE };
